import {
  AffordabilityRequest,
  ApplicantCountry,
  BdgAffordabilityRequest,
  BtlAffordabilityRequest,
  BtlApplicant,
  BtlType,
  HolidayLetFrequency,
  LendingTypeCode,
  LoanSecurity,
  MethodOfRepayment,
  MortgageType,
  ProductLengthExtended,
  ProductType,
  ProductTypeString,
  PropertyType,
  PurchaserType,
  RemortgageType,
  RepaymentMethod,
  RepaymentVehicle,
  SearchType,
  TaxStatusBtl,
  UkLocation,
} from 'apps/shared/src/models';

export class ProductsApplicant {
  public applicantNumber: number;
  public dateOfBirth?: Date | string | null;
  public countryResident?: ApplicantCountry | null;
  public grossAnnualIncome?: number | null;
  public taxStatusBtl?: TaxStatusBtl | null;
}

export class ProductsModelRequest {
  public productsSearchId: string;
  public caseId: string | null;
  public productType: LendingTypeCode;
  public productTypeExtended: ProductType;
  public newBuild?: boolean;
  public mortgageType: MortgageType;
  public propertyValue?: number;
  public loanAmount?: number;
  public outstandingBalanceCurrentMortgage?: number;
  public purchaserType?: PurchaserType;
  public repaymentMethod?: RepaymentMethod;
  public mortgageTermYears?: number;
  public mortgageTermMonths?: number;
  public interestOnlyAmount?: number;
  public productLengths?: ProductLengthExtended[];
  public rateTypes?: ProductTypeString[];
  public searchType: SearchType;
  public currentMortgageLender?: string;
  public productTransferInitialDate?: Date | string;
  public propertyType?: PropertyType | null;
  public productLengthFrom?: number;
  public productLengthTo?: number;
  public applicants: ProductsApplicant[];
  public numberOfApplicants: number | null;
  public lts?: number;
  public ltv?: number;
  public ltvDiscountedPrice?: number;
}

export class ProductsBdgModelRequest extends ProductsModelRequest {
  public affordabilitySearchId: string | null;
  public productType: LendingTypeCode;
  public regulated: boolean;
  public propertyValue?: number;
  public loanAmount?: number;
  public loanSecurity?: LoanSecurity;
  public currentMortgageBalanceOutstanding?: number;
  public methodOfRepayment?: MethodOfRepayment;
  public loanTerm?: number;
  public limitedCompany?: boolean;
  public repaymentMethod?: RepaymentMethod;
  public mortgageTermYears?: number;
  public productsSearchId: string;
  public searchType: SearchType;
  public mortgageType: MortgageType;
  public static mapAffordabilityToProductRequestBdg(request: BdgAffordabilityRequest, affordabilitySearchId: string) {
    return {
      productType: LendingTypeCode.Bdg,
      affordabilitySearchId: affordabilitySearchId,
      regulated: request.regulated,
      propertyValue: request.propertyValue,
      loanAmount: request.loanAmount,
      loanSecurity: request.loanSecurity,
      currentMortgageBalanceOutstanding: request.currentMortgageBalanceOutstanding,
      methodOfRepayment: request.methodOfRepayment,
      loanTerm: request.loanTerm,
      limitedCompany: request.limitedCompany,
      repaymentMethod: request.repaymentMethod,
      mortgageTermYears: request.mortgageTermYears,
      searchType: SearchType.Affordability,
      productsSearchId: affordabilitySearchId,
      mortgageType: MortgageType.Purchase,
    } as ProductsBdgModelRequest;
  }
}

export class ProductsResidentialModelRequest extends ProductsModelRequest {
  public remortgageType?: RemortgageType;
  public capitalRaising?: boolean;
  public helpToBuy?: boolean;
  public firstTimeBuyer?: boolean;
  public repaymentVehicle?: RepaymentVehicle;
  public equityValue: number;
  public purchasePriceShare?: number | null;
  public discountedPurchasePrice?: number;
  public additionalBorrowing?: boolean;
  public amountTransferredFromOtherLender?: number;
  public static mapAffordabilityToProductRequestResidential(
    request: AffordabilityRequest,
    affordabilitySearchId: string,
    caseId: string | null,
  ) {
    return {
      mortgageType: request.mortgageType,
      caseId,
      currentMortgageLender: request.currentMortgageLender,
      productTransferInitialDate: request.productTransferInitialDate,
      productType: LendingTypeCode.Res,
      productTypeExtended: request.productTypeExtended,
      newBuild: request.newBuild,
      propertyValue: request.propertyValue,
      loanAmount: request.loanAmount,
      productsSearchId: affordabilitySearchId,
      helpToBuy: request.helpToBuy,
      interestOnlyAmount: request.interestOnlyAmount,
      mortgageTermMonths: request.mortgageTermMonths,
      mortgageTermYears: request.mortgageTermYears,
      repaymentMethod: request.repaymentMethod,
      equityValue: request.equityValue,
      repaymentVehicle: request.repaymentVehicle,
      productLengths: [request.productLength as unknown as ProductLengthExtended],
      remortgageType: request.capitalRaising ? RemortgageType.CapitalRaising : null,
      searchType: SearchType.Affordability,
      currentMortgageBalance: request.amountTransferredFromOtherLender,
      propertyType: request.propertyType,
      firstTimeBuyer: request.purchaserType ?
        request.purchaserType === PurchaserType.FirstTimeBuyer : null,
      applicants: request.applicants,
      numberOfApplicants: request.numberOfApplicants,
      capitalRaising: request.capitalRaising,
    } as ProductsResidentialModelRequest;
  }
}

export class ProductsBtlModelRequest extends ProductsModelRequest {
  public limitedCompanyPurchase?: boolean;
  public limitedCompanySpecialPurposeVehicle?: boolean;
  public portfolioLandlord?: boolean;
  public numberOfProperties?: number;
  public btlType?: BtlType;
  public numberOfBedrooms?: number;
  public numberOfBlocks?: number;
  public propertySelfContainedFlats?: boolean;
  public remortgageType?: RemortgageType;
  public capitalRaising?: boolean;
  public firstTimeBuyer?: boolean;
  public firstTimeLandlord?: boolean;
  public ownerOccupier?: boolean;
  public holidayLetIncomeLow?: number;
  public holidayLetIncomeMedium?: number;
  public holidayLetIncomeHigh?: number;
  public holidayLetIncomeFrequency?: HolidayLetFrequency;
  public rentalAmountReceivedExpected?: number;
  public location?: UkLocation;

  public static mapAffordabilityToProductRequestBtl(
    request: BtlAffordabilityRequest,
    affordabilitySearchId: string,
    caseId: string | null,
  ) {
    return {
      caseId,
      productType: LendingTypeCode.Btl,
      productsSearchId: affordabilitySearchId,
      limitedCompanyPurchase: request.limitedCompanyPurchase,
      limitedCompanySpecialPurposeVehicle: request.limitedCompanySpecialPurposeVehicle,
      portfolioLandlord: request.portfolioLandlord,
      numberOfProperties: request.numberOfProperties,
      btlType: request.btlType,
      numberOfBedrooms: request.numberOfBedrooms,
      numberOfBlocks: request.numberOfBlocks,
      propertySelfContainedFlats: request.propertySelfContainedFlats,
      newBuild: request.newBuild,
      propertyType: request.propertyType,
      mortgageType: request.mortgageType,
      remortgageType: request.remortgageType,
      firstTimeBuyer: request.firstTimeBuyer,
      firstTimeLandlord: request.firstTimeLandlord,
      ownerOccupier: request.ownerOccupier,
      currentMortgageLender: request.currentMortgageLender,
      productTransferInitialDate: request.productTransferInitialDate,
      propertyValue: request.propertyValue,
      loanAmount: request.loanAmount,
      repaymentMethod: request.repaymentMethod,
      interestOnlyAmount: request.interestOnlyAmount,
      mortgageTermYears: request.mortgageTermYears,
      mortgageTermMonths: request.mortgageTermMonths,
      holidayLetIncomeLow: request.holidayLetIncomeLow,
      holidayLetIncomeMedium: request.holidayLetIncomeMedium,
      holidayLetIncomeHigh: request.holidayLetIncomeHigh,
      holidayLetIncomeFrequency: request.holidayLetIncomeFrequency,
      rentalAmountReceivedExpected: request.rentalAmountReceivedExpected,
      productLengths: [request.productLength as unknown as ProductLengthExtended],
      applicants: request.applicants.map((applicant: BtlApplicant) => {
        return {
          applicantNumber: applicant.applicantNumber,
          dateOfBirth: applicant.dateOfBirth,
          countryResident: applicant.countryResident,
          grossAnnualIncome: applicant.grossAnnualIncome,
          taxStatusBtl: applicant.taxStatus,
        } as ProductsApplicant;
      }),
      numberOfApplicants: request.applicants.length,
    } as ProductsBtlModelRequest;
  }
}

export interface ProductsBridgingModelRequest extends ProductsModelRequest {
  regulated: boolean;
  propertyValue?: number;
  loanAmount?: number;
  loanSecurity?: LoanSecurity;
  currentMortgageBalanceOutstanding?: number;
  methodOfRepayment?: MethodOfRepayment;
  loanTerm?: number;
  limitedCompany?: boolean;
}
